import { useState } from "react";
import { Link } from "@tanstack/react-router";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  Bell,
  CheckCheck,
  ShieldCheck,
  Sparkles,
  AlertTriangle,
  FileBarChart,
  Database,
  MessageSquare,
} from "lucide-react";

type NotifType = "approval" | "ai" | "alert" | "report" | "data" | "mention";
type Group = "New" | "Yesterday" | "Earlier";

type Notification = {
  id: string;
  type: NotifType;
  title: string;
  body: string;
  time: string;
  group: Group;
  unread: boolean;
  href?: string;
  actor: string;
};

const seed: Notification[] = [
  {
    id: "n1",
    type: "approval",
    title: "Action awaiting your approval",
    body: "Enroll 24 new managers into 'Strategic HRBP' journey.",
    time: "09:20 AM",
    group: "New",
    unread: true,
    href: "/actions",
    actor: "Cosmos AI",
  },
  {
    id: "n2",
    type: "ai",
    title: "Journey draft ready for review",
    body: "Tailor-made path for L&D Specialists generated from 14 modules.",
    time: "09:19 AM",
    group: "New",
    unread: true,
    href: "/journeys",
    actor: "Cosmos AI",
  },
  {
    id: "n3",
    type: "alert",
    title: "Data source degraded",
    body: "Workday connector latency above threshold (p95 4.8s).",
    time: "08:32 AM",
    group: "New",
    unread: true,
    href: "/admin",
    actor: "System",
  },
  {
    id: "n4",
    type: "report",
    title: "Weekly People Pulse is ready",
    body: "Scheduled report for People Analytics generated successfully.",
    time: "07:45 PM",
    group: "Yesterday",
    unread: false,
    href: "/reports",
    actor: "Reports",
  },
  {
    id: "n5",
    type: "mention",
    title: "Daniel mentioned you on a dashboard",
    body: "“@maya can you confirm the attrition cohort for Q3?”",
    time: "07:32 PM",
    group: "Yesterday",
    unread: false,
    href: "/dashboards",
    actor: "Daniel R.",
  },
  {
    id: "n6",
    type: "data",
    title: "New dataset available",
    body: "fct_learning_completions refreshed with 2,431 new rows.",
    time: "06:27 PM",
    group: "Yesterday",
    unread: false,
    href: "/data-explorer",
    actor: "Data Platform",
  },
];

const typeMeta: Record<NotifType, { icon: typeof Bell; tone: string }> = {
  approval: { icon: ShieldCheck, tone: "bg-[var(--warning)]/15 text-[var(--warning)]" },
  ai: { icon: Sparkles, tone: "bg-[var(--ai)]/15 text-[var(--ai)]" },
  alert: { icon: AlertTriangle, tone: "bg-[var(--danger)]/15 text-[var(--danger)]" },
  report: { icon: FileBarChart, tone: "bg-[var(--primary)]/15 text-[var(--primary)]" },
  data: { icon: Database, tone: "bg-[var(--success)]/15 text-[var(--success)]" },
  mention: { icon: MessageSquare, tone: "bg-muted text-anchor" },
};

const groupOrder: Group[] = ["New", "Yesterday", "Earlier"];

export function NotificationsPopover() {
  const [items, setItems] = useState<Notification[]>(seed);
  const [tab, setTab] = useState<"all" | "updates">("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [open, setOpen] = useState(false);

  const unreadCount = items.filter((i) => i.unread).length;
  const filtered = unreadOnly ? items.filter((i) => i.unread) : items;

  const markAllRead = () => setItems((xs) => xs.map((x) => ({ ...x, unread: false })));
  const markRead = (id: string) =>
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, unread: false } : x)));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "h-9 w-9 grid place-items-center rounded-md text-anchor relative transition-colors",
            "hover:bg-muted",
            open && "bg-muted",
          )}
          aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-[var(--danger)] ring-2 ring-surface" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="end"
          sideOffset={10}
          collisionPadding={12}
          className={cn(
            "z-50 w-[420px] p-0 overflow-hidden bg-surface text-popover-foreground",
            "rounded-2xl",
            "shadow-[0_30px_80px_-20px_rgba(23,15,63,0.35),0_8px_24px_-12px_rgba(23,15,63,0.18)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-1 origin-top-right",
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-3">
            <div className="text-[15px] font-semibold text-anchor tracking-tight">
              {unreadCount > 0 ? `${unreadCount} Notifications` : "Notifications"}
            </div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <span className="text-[11px] text-muted-foreground">Unread</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={unreadOnly}
                  onClick={() => setUnreadOnly((v) => !v)}
                  className={cn(
                    "relative h-[18px] w-[30px] rounded-full transition-colors",
                    unreadOnly ? "bg-[var(--primary)]" : "bg-muted",
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform",
                      unreadOnly ? "translate-x-[13px]" : "translate-x-0.5",
                    )}
                  />
                </button>
              </label>
              <button
                onClick={markAllRead}
                disabled={unreadCount === 0}
                aria-label="Mark all as read"
                className="h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:text-[var(--primary)] hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground transition-colors"
              >
                <CheckCheck className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-5">
            <div className="flex items-center gap-5">
              {(["all", "updates"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={cn(
                    "relative pb-2.5 text-[13px] font-medium capitalize transition-colors",
                    tab === t ? "text-[var(--primary)]" : "text-muted-foreground hover:text-anchor",
                  )}
                >
                  {t}
                  {tab === t && (
                    <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[var(--primary)] rounded-full" />
                  )}
                </button>
              ))}
            </div>
            <div className="h-px bg-[color:var(--border-soft)]/60" />
          </div>

          {/* List */}
          <ScrollArea className="max-h-[480px]">
            {filtered.length === 0 ? (
              <div className="px-4 py-14 text-center">
                <div className="mx-auto h-10 w-10 rounded-full bg-muted grid place-items-center mb-3">
                  <CheckCheck className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="text-sm font-medium text-anchor">You're all caught up</div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  We'll let you know when something needs your attention.
                </div>
              </div>
            ) : (
              <div className="pb-2">
                {groupOrder.map((g) => {
                  const groupItems = filtered.filter((i) => i.group === g);
                  if (groupItems.length === 0) return null;
                  return (
                    <div key={g}>
                      <div className="px-5 pt-4 pb-1.5 text-[11px] font-medium text-muted-foreground tracking-wide">
                        {g}
                      </div>
                      <ul>
                        {groupItems.map((n) => {
                          const meta = typeMeta[n.type];
                          const Icon = meta.icon;
                          const handleClick = () => {
                            markRead(n.id);
                            setOpen(false);
                          };
                          const inner = (
                            <div className="group relative flex gap-3 px-5 py-2.5 hover:bg-muted/50 transition-colors cursor-pointer">
                              {n.unread && (
                                <span className="absolute left-1.5 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-[var(--primary)]" />
                              )}
                              <div className="relative shrink-0">
                                <div className="h-9 w-9 rounded-full bg-muted grid place-items-center text-[11px] font-semibold text-anchor">
                                  {n.actor
                                    .split(" ")
                                    .map((p) => p[0])
                                    .slice(0, 2)
                                    .join("")}
                                </div>
                                <span
                                  className={cn(
                                    "absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full grid place-items-center ring-2 ring-surface",
                                    meta.tone,
                                  )}
                                >
                                  <Icon className="h-2.5 w-2.5" />
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start gap-2">
                                  <div className="text-[13px] text-anchor leading-snug flex-1 min-w-0">
                                    <span className="font-semibold">'{n.title}'</span>
                                  </div>
                                  <div className="text-[11px] text-muted-foreground shrink-0 tabular-nums">
                                    {n.time}
                                  </div>
                                </div>
                                <div className="text-[12px] text-muted-foreground mt-0.5 line-clamp-1">
                                  {n.body}
                                </div>
                              </div>
                            </div>
                          );
                          return (
                            <li key={n.id}>
                              {n.href ? (
                                <Link to={n.href as never} onClick={handleClick} className="block">
                                  {inner}
                                </Link>
                              ) : (
                                <button onClick={handleClick} className="w-full text-left">
                                  {inner}
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </Popover>
  );
}
